import React, { useState, useEffect } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Organization";
import UploadAvatar from "../../../../components/uploadAvatar";
import { organizationService } from "../../../../services/organization.service";
import { useCombinedStore } from "../../../../store";

interface OrganizationProps {
  isEditing: boolean;
  formData: any;
  decryptedInfoToken: any;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ) => void;
  handleSave: (objectType: any) => void;
  updateState: (newState: any) => void;
  expandedAccordion: string | null;
  toggleAccordion: (accordionKey: string) => void;
}

const Organization: React.FC<OrganizationProps> = ({
  isEditing,
  formData,
  decryptedInfoToken,
  handleInputChange,
  handleSave,
  updateState,
  expandedAccordion,
  toggleAccordion,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [tempImage, setTempImage] = useState<string>("");
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [displayImage, setDisplayImage] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const { authUserZustand, setAuthUserZustand } = useCombinedStore((state) => {
    return {
      authUserZustand: state.authUserZustand,
      setAuthUserZustand: state.setAuthUserZustand,
    };
  });

  const organization = decryptedInfoToken.organization;

  // Helper para pegar valores de organizationInfo
  const getInfoValue = (fieldName: string) => {
    const infoField = organization?.organizationInfo?.find(
      (info: any) => info.organizationInfoName === fieldName,
    );
    return infoField?.organizationInfoValue || "";
  };

  useEffect(() => {
    const currentImage = organization?.organizationLogoUrl || "";
    if (currentImage && currentImage.startsWith("http")) {
      setDisplayImage(currentImage);
    } else if (currentImage && currentImage.startsWith("data:")) {
      setDisplayImage(currentImage);
    } else if (currentImage) {
      setDisplayImage(`data:image/jpeg;base64,${currentImage}`);
    } else {
      setDisplayImage("");
    }
  }, [organization]);

  // Validação em tempo real
  const validateField = (field: string, value: string): boolean => {
    if (field === "organizationName") {
      return value.trim().length > 0;
    }
    if (field === "organizationEmail") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    }
    if (field === "organizationWebsite") {
      if (!value) return true; // Campo opcional
      const urlRegex =
        /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      return urlRegex.test(value);
    }
    if (field === "organizationPhone") {
      if (!value) return true; // Campo opcional
      const phoneRegex =
        /^\(?[1-9]{2}\)? ?(?:[2-8]|9[1-9])[0-9]{3}\-?[0-9]{4}$/;
      return phoneRegex.test(value.replace(/\D/g, ""));
    }
    return true;
  };

  const handleInputChangeWithValidation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ) => {
    const value = e.target.value;
    handleInputChange(e, field);

    // Validação em tempo real para campos importantes
    if (isEditing) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: !validateField(field, value),
      }));
    }
  };

  const handleImageUpload = async (image: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      setTempImageFile(image);

      // Cria preview da imagem
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result as string;
        setTempImage(base64Image);
        setDisplayImage(base64Image);
      };
      reader.readAsDataURL(image);

      // Simula progresso
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      if (!isEditing) {
        await saveOrganizationLogo(image);
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 500);
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      setIsUploading(false);
      setUploadProgress(0);
      updateState({
        message: "Erro ao processar logo. Tente novamente.",
        messageType: "error",
      });
    }
  };

  const saveOrganizationLogo = async (image: File) => {
    try {
      setIsSaving(true);

      const organizationData = {
        organizationUuid: organization.organizationUuid,
      };

      const response = await organizationService.updateOrganizationLogo(
        image,
        organizationData,
      );

      if (!response) {
        throw new Error("Token não recebido após atualização do logo");
      }

      setAuthUserZustand({
        authUser: {
          ...authUserZustand,
          Token: response,
        },
      });

      updateState({
        message: "Logo da organização atualizado com sucesso!",
        messageType: "success",
      });

      setTempImage("");
      setTempImageFile(null);
    } catch (error: any) {
      console.error("Erro ao atualizar logo:", error);
      updateState({
        message:
          error.response?.data?.message ||
          error.message ||
          "Erro ao atualizar logo. Tente novamente.",
        messageType: "error",
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveOrganization = async () => {
    // Validação antes de salvar
    const errors: Record<string, boolean> = {};
    if (!formData.organizationName || formData.organizationName.trim() === "") {
      errors.organizationName = true;
    }
    if (
      formData.organizationEmail &&
      !validateField("organizationEmail", formData.organizationEmail)
    ) {
      errors.organizationEmail = true;
    }
    if (
      formData.organizationWebsite &&
      !validateField("organizationWebsite", formData.organizationWebsite)
    ) {
      errors.organizationWebsite = true;
    }
    if (
      formData.organizationPhone &&
      !validateField("organizationPhone", formData.organizationPhone)
    ) {
      errors.organizationPhone = true;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      updateState({
        message: "Por favor, corrija os campos com erro.",
        messageType: "error",
      });
      return;
    }

    try {
      setIsSaving(true);

      const updateData: any = {
        organizationUuid: organization.organizationUuid,
      };

      // Campos da tabela ActOrganization
      if (
        formData.organizationName &&
        formData.organizationName !== organization.organizationName
      ) {
        updateData.organizationName = formData.organizationName;
      }
      if (
        formData.organizationTaxPayerID &&
        formData.organizationTaxPayerID !== organization.organizationTaxPayerID
      ) {
        updateData.organizationTaxPayerID = formData.organizationTaxPayerID;
      }
      if (
        formData.organizationAddress &&
        formData.organizationAddress !== organization.organizationAddress
      ) {
        updateData.organizationAddress = formData.organizationAddress;
      }

      // Campos para ActOrganizationInfo
      if (
        formData.organizationEmail &&
        formData.organizationEmail !== getInfoValue("organizationEmail")
      ) {
        updateData.organizationEmail = formData.organizationEmail;
      }
      if (
        formData.organizationPhone &&
        formData.organizationPhone !== getInfoValue("organizationPhone")
      ) {
        updateData.organizationPhone = formData.organizationPhone;
      }
      if (
        formData.organizationDescription !==
        getInfoValue("organizationDescription")
      ) {
        updateData.organizationDescription =
          formData.organizationDescription || null;
      }
      if (
        formData.organizationWebsite &&
        formData.organizationWebsite !== getInfoValue("organizationWebsite")
      ) {
        updateData.organizationWebsite = formData.organizationWebsite;
      }
      if (
        formData.organizationSocialMedia &&
        formData.organizationSocialMedia !==
          getInfoValue("organizationSocialMedia")
      ) {
        updateData.organizationSocialMedia = formData.organizationSocialMedia;
      }
      if (
        formData.organizationBusinessHours &&
        formData.organizationBusinessHours !==
          getInfoValue("organizationBusinessHours")
      ) {
        updateData.organizationBusinessHours =
          formData.organizationBusinessHours;
      }
      if (
        formData.organizationFoundedYear &&
        formData.organizationFoundedYear !==
          getInfoValue("organizationFoundedYear")
      ) {
        updateData.organizationFoundedYear = formData.organizationFoundedYear;
      }

      if (Object.keys(updateData).length > 1) {
        const response =
          await organizationService.updateOrganization(updateData);

        if (!response) {
          throw new Error("Token não recebido após atualização");
        }

        setAuthUserZustand({
          authUser: {
            ...authUserZustand,
            Token: response,
          },
        });
      }

      handleSave("company");
      setFieldErrors({});
      updateState({
        message: "Informações da empresa salvas com sucesso!",
        messageType: "success",
        isEditing: false,
      });
    } catch (error: any) {
      console.error("Erro ao salvar dados da empresa:", error);
      updateState({
        message:
          error.response?.data?.message ||
          "Erro ao salvar dados. Tente novamente.",
        messageType: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFieldErrors({});
    updateState({
      isEditing: false,
    });
  };

  const getStatusBadge = (status: boolean) => (
    <AlfredoStyles.StatusBadge $isActive={status}>
      <AlfredoStyles.MaterialIcons>
        {status ? "check_circle" : "cancel"}
      </AlfredoStyles.MaterialIcons>
      {status ? "Ativa" : "Inativa"}
    </AlfredoStyles.StatusBadge>
  );

  return (
    <AlfredoStyles.FieldsContainer>
      {/* Cabeçalho com Logo e Info */}
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.LogoSection>
            <AlfredoStyles.AvatarWrapper $isUploading={isUploading || isSaving}>
              <UploadAvatar
                onImageConfirmed={handleImageUpload}
                imageType={2}
                initialImage={displayImage}
              />
              {(isSaving || isUploading) && (
                <AlfredoStyles.LoadingOverlay>
                  <AlfredoStyles.LoadingContent>
                    {isUploading ? (
                      <>
                        <AlfredoStyles.LoadingSpinner />
                        <div>Enviando: {uploadProgress}%</div>
                        <AlfredoStyles.LoadingSubtext>
                          Fazendo upload para nuvem...
                        </AlfredoStyles.LoadingSubtext>
                      </>
                    ) : (
                      <>
                        <AlfredoStyles.LoadingSpinner />
                        <div>Salvando...</div>
                      </>
                    )}
                  </AlfredoStyles.LoadingContent>
                </AlfredoStyles.LoadingOverlay>
              )}
            </AlfredoStyles.AvatarWrapper>
            {!isEditing && (
              <AlfredoStyles.AvatarHint>
                Clique no logo para alterar
              </AlfredoStyles.AvatarHint>
            )}
          </AlfredoStyles.LogoSection>

          <AlfredoStyles.OrganizationInfo>
            <AlfredoStyles.OrganizationTitle>
              {organization.organizationName || "Sua Empresa"}
            </AlfredoStyles.OrganizationTitle>
            <AlfredoStyles.OrganizationSubtitle>
              Gerencie sua rede de estabelecimentos
            </AlfredoStyles.OrganizationSubtitle>
          </AlfredoStyles.OrganizationInfo>
        </AlfredoStyles.HeaderContent>

        <AlfredoStyles.ButtonWrapper style={{ margin: 0 }}>
          {isEditing ? (
            <>
              <AlfredoStyles.Button
                onClick={handleSaveOrganization}
                disabled={isSaving || isUploading}
              >
                {isSaving ? (
                  <>
                    <AlfredoStyles.ButtonSpinner />
                    Salvando...
                  </>
                ) : (
                  <>
                    <AlfredoStyles.MaterialIcons>
                      save
                    </AlfredoStyles.MaterialIcons>
                    Salvar Alterações
                  </>
                )}
              </AlfredoStyles.Button>
              <AlfredoStyles.SecondaryButton
                onClick={handleCancel}
                disabled={isSaving || isUploading}
              >
                <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
                Cancelar
              </AlfredoStyles.SecondaryButton>
            </>
          ) : (
            <AlfredoStyles.Button
              onClick={() => updateState({ isEditing: true })}
              disabled={isUploading}
            >
              <AlfredoStyles.MaterialIcons>edit</AlfredoStyles.MaterialIcons>
              Editar Empresa
            </AlfredoStyles.Button>
          )}
        </AlfredoStyles.ButtonWrapper>
      </AlfredoStyles.Header>

      {(tempImage || tempImageFile) && !isSaving && !isUploading && (
        <AlfredoStyles.SuccessBanner>
          <AlfredoStyles.MaterialIcons>
            check_circle
          </AlfredoStyles.MaterialIcons>
          <div>
            <AlfredoStyles.SuccessBannerTitle>
              Novo logo selecionado
            </AlfredoStyles.SuccessBannerTitle>
            <AlfredoStyles.SuccessBannerText>
              {!isEditing
                ? "Logo salvo automaticamente no armazenamento em nuvem."
                : "Clique em 'Salvar Alterações' para confirmar."}
            </AlfredoStyles.SuccessBannerText>
          </div>
        </AlfredoStyles.SuccessBanner>
      )}

      {/* Cards de Métricas da Rede */}
      <AlfredoStyles.MetricsGrid>
        <AlfredoStyles.MetricCard>
          <AlfredoStyles.MetricIcon>🏢</AlfredoStyles.MetricIcon>
          <AlfredoStyles.MetricValue>
            {organization.companies?.length || 0}
          </AlfredoStyles.MetricValue>
          <AlfredoStyles.MetricLabel>
            Estabelecimentos
          </AlfredoStyles.MetricLabel>
        </AlfredoStyles.MetricCard>

        <AlfredoStyles.MetricCard>
          <AlfredoStyles.MetricIcon>🏪</AlfredoStyles.MetricIcon>
          <AlfredoStyles.MetricValue $color="#28a745">
            {organization.companies?.reduce(
              (acc: number, company: any) =>
                acc + (company.subsidiaries?.length || 0),
              0,
            ) || 0}
          </AlfredoStyles.MetricValue>
          <AlfredoStyles.MetricLabel>Lojas Ativas</AlfredoStyles.MetricLabel>
        </AlfredoStyles.MetricCard>

        <AlfredoStyles.MetricCard>
          <AlfredoStyles.MetricIcon>📦</AlfredoStyles.MetricIcon>
          <AlfredoStyles.MetricValue $color="#ffc107">
            {organization.companies?.reduce(
              (acc: number, company: any) =>
                acc +
                company.subsidiaries?.reduce(
                  (subAcc: number, subsidiary: any) =>
                    subAcc + (subsidiary.ambients?.length || 0),
                  0,
                ),
              0,
            ) || 0}
          </AlfredoStyles.MetricValue>
          <AlfredoStyles.MetricLabel>Ambientes</AlfredoStyles.MetricLabel>
        </AlfredoStyles.MetricCard>

        <AlfredoStyles.MetricCard>
          <AlfredoStyles.MetricIcon>🛒</AlfredoStyles.MetricIcon>
          <AlfredoStyles.MetricValue $color="#dc3545">
            {organization.companies?.reduce(
              (acc: number, company: any) =>
                acc +
                company.subsidiaries?.reduce(
                  (subAcc: number, subsidiary: any) =>
                    subAcc +
                    subsidiary.ambients?.reduce(
                      (ambAcc: number, ambient: any) =>
                        ambAcc + (ambient.items?.length || 0),
                      0,
                    ),
                  0,
                ),
              0,
            ) || 0}
          </AlfredoStyles.MetricValue>
          <AlfredoStyles.MetricLabel>
            Itens Cadastrados
          </AlfredoStyles.MetricLabel>
        </AlfredoStyles.MetricCard>
      </AlfredoStyles.MetricsGrid>

      {/* Informações da Empresa */}
      <AlfredoStyles.Section>
        <AlfredoStyles.SectionHeader>
          <AlfredoStyles.SectionTitle>
            <AlfredoStyles.MaterialIcons>business</AlfredoStyles.MaterialIcons>
            Informações da Empresa
          </AlfredoStyles.SectionTitle>
          {getStatusBadge(organization.organizationStatus !== false)}
        </AlfredoStyles.SectionHeader>

        <AlfredoStyles.SectionContent>
          {isEditing ? (
            <AlfredoStyles.FormGrid>
              <AlfredoStyles.InputWrapper>
                <AlfredoStyles.Label $required>
                  <AlfredoStyles.MaterialIcons>
                    business
                  </AlfredoStyles.MaterialIcons>
                  Nome da Empresa
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="text"
                  value={
                    formData.organizationName ||
                    organization.organizationName ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChangeWithValidation(e, "organizationName")
                  }
                  placeholder="Nome da empresa"
                  $hasError={fieldErrors.organizationName}
                />
                {fieldErrors.organizationName && (
                  <AlfredoStyles.ErrorText>
                    Nome da empresa é obrigatório
                  </AlfredoStyles.ErrorText>
                )}
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    badge
                  </AlfredoStyles.MaterialIcons>
                  CNPJ
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="text"
                  value={
                    formData.organizationTaxPayerID ||
                    organization.organizationTaxPayerID ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(e, "organizationTaxPayerID")
                  }
                  placeholder="00.000.000/0000-00"
                />
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    email
                  </AlfredoStyles.MaterialIcons>
                  Email
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="email"
                  value={
                    formData.organizationEmail ||
                    getInfoValue("organizationEmail") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChangeWithValidation(e, "organizationEmail")
                  }
                  placeholder="contato@empresa.com.br"
                  $hasError={fieldErrors.organizationEmail}
                />
                {fieldErrors.organizationEmail && (
                  <AlfredoStyles.ErrorText>
                    Email inválido
                  </AlfredoStyles.ErrorText>
                )}
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    phone
                  </AlfredoStyles.MaterialIcons>
                  Telefone
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="tel"
                  value={
                    formData.organizationPhone ||
                    getInfoValue("organizationPhone") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChangeWithValidation(e, "organizationPhone")
                  }
                  placeholder="(11) 99999-9999"
                  $hasError={fieldErrors.organizationPhone}
                />
                {fieldErrors.organizationPhone && (
                  <AlfredoStyles.ErrorText>
                    Telefone inválido
                  </AlfredoStyles.ErrorText>
                )}
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper style={{ gridColumn: "1 / -1" }}>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    location_on
                  </AlfredoStyles.MaterialIcons>
                  Endereço Matriz
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="text"
                  value={
                    formData.organizationAddress ||
                    organization.organizationAddress ||
                    ""
                  }
                  onChange={(e) => handleInputChange(e, "organizationAddress")}
                  placeholder="Rua, número, bairro, cidade - UF"
                />
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper style={{ gridColumn: "1 / -1" }}>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    description
                  </AlfredoStyles.MaterialIcons>
                  Sobre a Empresa
                </AlfredoStyles.Label>
                <AlfredoStyles.TextArea
                  value={
                    formData.organizationDescription ||
                    getInfoValue("organizationDescription") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(e, "organizationDescription")
                  }
                  placeholder="Breve descrição sobre a empresa, missão, valores..."
                  rows={4}
                />
              </AlfredoStyles.InputWrapper>
            </AlfredoStyles.FormGrid>
          ) : (
            <AlfredoStyles.InfoGrid>
              <AlfredoStyles.InfoItem>
                <AlfredoStyles.InfoLabel>
                  <AlfredoStyles.MaterialIcons>
                    business
                  </AlfredoStyles.MaterialIcons>
                  Nome
                </AlfredoStyles.InfoLabel>
                <AlfredoStyles.InfoValue>
                  {organization.organizationName || "-"}
                </AlfredoStyles.InfoValue>
              </AlfredoStyles.InfoItem>

              <AlfredoStyles.InfoItem>
                <AlfredoStyles.InfoLabel>
                  <AlfredoStyles.MaterialIcons>
                    badge
                  </AlfredoStyles.MaterialIcons>
                  CNPJ
                </AlfredoStyles.InfoLabel>
                <AlfredoStyles.InfoValue>
                  {organization.organizationTaxPayerID || "-"}
                </AlfredoStyles.InfoValue>
              </AlfredoStyles.InfoItem>

              <AlfredoStyles.InfoItem>
                <AlfredoStyles.InfoLabel>
                  <AlfredoStyles.MaterialIcons>
                    email
                  </AlfredoStyles.MaterialIcons>
                  Email
                </AlfredoStyles.InfoLabel>
                <AlfredoStyles.InfoValue>
                  {getInfoValue("organizationEmail") || "-"}
                </AlfredoStyles.InfoValue>
              </AlfredoStyles.InfoItem>

              <AlfredoStyles.InfoItem>
                <AlfredoStyles.InfoLabel>
                  <AlfredoStyles.MaterialIcons>
                    phone
                  </AlfredoStyles.MaterialIcons>
                  Telefone
                </AlfredoStyles.InfoLabel>
                <AlfredoStyles.InfoValue>
                  {getInfoValue("organizationPhone") || "-"}
                </AlfredoStyles.InfoValue>
              </AlfredoStyles.InfoItem>

              <AlfredoStyles.InfoItem style={{ gridColumn: "1 / -1" }}>
                <AlfredoStyles.InfoLabel>
                  <AlfredoStyles.MaterialIcons>
                    location_on
                  </AlfredoStyles.MaterialIcons>
                  Endereço
                </AlfredoStyles.InfoLabel>
                <AlfredoStyles.InfoValue>
                  {organization.organizationAddress || "-"}
                </AlfredoStyles.InfoValue>
              </AlfredoStyles.InfoItem>

              {getInfoValue("organizationDescription") && (
                <AlfredoStyles.InfoItem style={{ gridColumn: "1 / -1" }}>
                  <AlfredoStyles.InfoLabel>
                    <AlfredoStyles.MaterialIcons>
                      description
                    </AlfredoStyles.MaterialIcons>
                    Sobre
                  </AlfredoStyles.InfoLabel>
                  <AlfredoStyles.InfoValue>
                    {getInfoValue("organizationDescription")}
                  </AlfredoStyles.InfoValue>
                </AlfredoStyles.InfoItem>
              )}
            </AlfredoStyles.InfoGrid>
          )}
        </AlfredoStyles.SectionContent>
      </AlfredoStyles.Section>

      {/* Seção de Informações Adicionais */}
      <AlfredoStyles.Section>
        <AlfredoStyles.SectionHeader>
          <AlfredoStyles.SectionTitle>
            <AlfredoStyles.MaterialIcons>info</AlfredoStyles.MaterialIcons>
            Informações Adicionais
          </AlfredoStyles.SectionTitle>
        </AlfredoStyles.SectionHeader>

        <AlfredoStyles.SectionContent>
          {isEditing ? (
            <AlfredoStyles.FormGrid>
              <AlfredoStyles.InputWrapper>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    language
                  </AlfredoStyles.MaterialIcons>
                  Website
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="url"
                  value={
                    formData.organizationWebsite ||
                    getInfoValue("organizationWebsite") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChangeWithValidation(e, "organizationWebsite")
                  }
                  placeholder="https://www.seusite.com.br"
                  $hasError={fieldErrors.organizationWebsite}
                />
                {fieldErrors.organizationWebsite && (
                  <AlfredoStyles.ErrorText>
                    URL inválida
                  </AlfredoStyles.ErrorText>
                )}
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    calendar_today
                  </AlfredoStyles.MaterialIcons>
                  Ano de Fundação
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="number"
                  value={
                    formData.organizationFoundedYear ||
                    getInfoValue("organizationFoundedYear") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(e, "organizationFoundedYear")
                  }
                  placeholder="2010"
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper style={{ gridColumn: "1 / -1" }}>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    schedule
                  </AlfredoStyles.MaterialIcons>
                  Horário de Atendimento Corporativo
                </AlfredoStyles.Label>
                <AlfredoStyles.Input
                  type="text"
                  value={
                    formData.organizationBusinessHours ||
                    getInfoValue("organizationBusinessHours") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(e, "organizationBusinessHours")
                  }
                  placeholder="Segunda a Sexta, 9h às 18h"
                />
              </AlfredoStyles.InputWrapper>

              <AlfredoStyles.InputWrapper style={{ gridColumn: "1 / -1" }}>
                <AlfredoStyles.Label>
                  <AlfredoStyles.MaterialIcons>
                    share
                  </AlfredoStyles.MaterialIcons>
                  Redes Sociais
                </AlfredoStyles.Label>
                <AlfredoStyles.TextArea
                  value={
                    formData.organizationSocialMedia ||
                    getInfoValue("organizationSocialMedia") ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(e, "organizationSocialMedia")
                  }
                  placeholder="Instagram: @empresa, Facebook: /empresa, LinkedIn: /company/empresa"
                  rows={3}
                />
              </AlfredoStyles.InputWrapper>
            </AlfredoStyles.FormGrid>
          ) : (
            <AlfredoStyles.InfoGrid>
              {getInfoValue("organizationWebsite") && (
                <AlfredoStyles.InfoItem>
                  <AlfredoStyles.InfoLabel>
                    <AlfredoStyles.MaterialIcons>
                      language
                    </AlfredoStyles.MaterialIcons>
                    Website
                  </AlfredoStyles.InfoLabel>
                  <AlfredoStyles.InfoValue>
                    <a
                      href={getInfoValue("organizationWebsite")}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#007bff", textDecoration: "none" }}
                    >
                      {getInfoValue("organizationWebsite")}
                    </a>
                  </AlfredoStyles.InfoValue>
                </AlfredoStyles.InfoItem>
              )}

              {getInfoValue("organizationFoundedYear") && (
                <AlfredoStyles.InfoItem>
                  <AlfredoStyles.InfoLabel>
                    <AlfredoStyles.MaterialIcons>
                      calendar_today
                    </AlfredoStyles.MaterialIcons>
                    Fundação
                  </AlfredoStyles.InfoLabel>
                  <AlfredoStyles.InfoValue>
                    {getInfoValue("organizationFoundedYear")}
                  </AlfredoStyles.InfoValue>
                </AlfredoStyles.InfoItem>
              )}

              {getInfoValue("organizationBusinessHours") && (
                <AlfredoStyles.InfoItem style={{ gridColumn: "1 / -1" }}>
                  <AlfredoStyles.InfoLabel>
                    <AlfredoStyles.MaterialIcons>
                      schedule
                    </AlfredoStyles.MaterialIcons>
                    Horário de Atendimento
                  </AlfredoStyles.InfoLabel>
                  <AlfredoStyles.InfoValue>
                    {getInfoValue("organizationBusinessHours")}
                  </AlfredoStyles.InfoValue>
                </AlfredoStyles.InfoItem>
              )}

              {getInfoValue("organizationSocialMedia") && (
                <AlfredoStyles.InfoItem style={{ gridColumn: "1 / -1" }}>
                  <AlfredoStyles.InfoLabel>
                    <AlfredoStyles.MaterialIcons>
                      share
                    </AlfredoStyles.MaterialIcons>
                    Redes Sociais
                  </AlfredoStyles.InfoLabel>
                  <AlfredoStyles.InfoValue>
                    {getInfoValue("organizationSocialMedia")}
                  </AlfredoStyles.InfoValue>
                </AlfredoStyles.InfoItem>
              )}
            </AlfredoStyles.InfoGrid>
          )}
        </AlfredoStyles.SectionContent>
      </AlfredoStyles.Section>
    </AlfredoStyles.FieldsContainer>
  );
};

export default Organization;
